import React from 'react';
import useWebSocket, {type ReadyState} from 'react-use-websocket';
import moment from 'moment-timezone';

import {useResilientQuery} from '@famly/stat_apollo-query';

import {type GetUnlockEventsJWTQuery, type GetUnlockEventsJWTQueryVariables} from './__generated__/queries.api-types';
import * as Queries from './queries';

type Message =
    | {
          type: 'UnlockSignInDevice';
          pin?: string;
      }
    | {
          type: 'Unsubscribe';
      };

const parseJwt = (token: string) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

const isJwtExpired = (token?: string | null) => {
    if (!token) {
        return false;
    }
    const decodedToken = parseJwt(token);
    return moment.unix(decodedToken.exp).isBefore(moment());
};

/**
 * Listen for biometric events to unlock signin app
 *
 * @param userDetectedCallBack Callback to call when a user is detected. The function will receive the pinCode of of the user
 * @returns An object containing whether the socket is open, if the client i subscribed to events and whether doorlocking is enabled at all.
 */
export function useListenForBiometricEvents(userDetectedCallBack: (pinCode: string) => void): {
    connectionState: ReadyState;
    isSubscribed: boolean;
    enabled: boolean;
} {
    const {data: unlockEventsJWT, refetch} = useResilientQuery<
        GetUnlockEventsJWTQuery,
        GetUnlockEventsJWTQueryVariables
    >(Queries.GetUnlockEventsJWT, {fetchPolicy: 'network-only' as const});

    // If the JWT is not available, we do not want the websocket to open. If the url is null, the useWebSocket hook will not create the connection
    const socketUrl = unlockEventsJWT?.doorLocking.subscribeToUnlockEvents ? 'wss://ws.famly.co/ws' : null;

    const {sendMessage, lastMessage, readyState} = useWebSocket(socketUrl, {
        share: true, // We want to share the same connection across all the different subscribers
        retryOnError: true,
        shouldReconnect: () => {
            const tokenExpired = isJwtExpired(unlockEventsJWT?.doorLocking.subscribeToUnlockEvents);

            // refresh token if it is expired, restarting the connection by finishing the fetching
            if (tokenExpired) {
                refetch();
            }

            // keep open if not expired, close otherwise
            return !tokenExpired;
        },
        onOpen: () => {
            if (unlockEventsJWT?.doorLocking.subscribeToUnlockEvents) {
                sendMessage(unlockEventsJWT.doorLocking.subscribeToUnlockEvents);
            }
        },
        reconnectAttempts: 30,
        reconnectInterval: 3000,
    });

    React.useEffect(() => {
        if (lastMessage === null) {
            return;
        }

        const parsedData: Message = JSON.parse(lastMessage.data);
        if (parsedData.type === 'UnlockSignInDevice' && parsedData.pin) {
            userDetectedCallBack(parsedData.pin);
        }
    }, [lastMessage, userDetectedCallBack]);

    return React.useMemo(
        () => ({
            connectionState: readyState,
            isSubscribed: lastMessage?.data && JSON.parse(lastMessage.data)?.type === 'Unsubscribe',
            enabled: Boolean(unlockEventsJWT?.doorLocking.subscribeToUnlockEvents),
        }),
        [readyState, lastMessage?.data, unlockEventsJWT?.doorLocking.subscribeToUnlockEvents],
    );
}
